// Sprint ONBOARD ONB.B1 — Dashboard onboarding panel.
//
// Surfaces the active Setup Project to the Buyer (and Tech Lead) on the
// dashboard. Polls GET /dashboard/setup-project at 10s; renders nothing
// when the API returns 204 (no active setup_project → auto-hide).
//
// Polling-cadence note (operator-flagged 2026-05-23):
// NotificationBell polls /notifications/unread-count at 30s; this panel
// polls /dashboard/setup-project at 10s. Combined ~5 fetches/min per
// dashboard mount. Acceptable at current scale; revisit if a third
// polling component lands on the dashboard.
//
// CTA contract (rewritten 2026-05-24 for ONB.B1-FIX.A1):
// Task advancement is SERVER-SIDE. The panel is a pure observer of
// setup_tasks status via the 10s poll. CTA behaviour for the primary task:
//   • invite_technical_lead → opens the in-panel invite modal (below).
//     Submit POSTs to /setup-projects/:id/invite-technical-lead, which
//     atomically writes setup_invitations AND advances
//     setup_tasks.invite_technical_lead to in_progress in one transaction.
//     The 10s poll picks up the status change on the next tick (the modal
//     also triggers an immediate refetch for snappier UX).
//   • all other taskTypes → navigate to TASK_COPY[taskType].ctaPath.
//     Those destination pages are expected to call the relevant backend
//     endpoint which is responsible for its own task advancement. (Each
//     such wiring story is filed separately; today only the
//     invite_technical_lead path has its backend wiring complete.)

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
// ONB.C1 (A.3) — task copy + label/pill maps + task-type unions promoted to a
// shared module so the dashboard panel and the focused /setup SetupWizard never
// drift. NOTE: connect_github/authorize_gdrive ctaPaths now point at the /setup
// sub-wizards (was /settings/byoi) — intended panel behaviour change per V3.
import {
  TASK_COPY,
  PHASE_LABELS,
  STATUS_PILL,
  type SetupTaskType,
  type SetupTaskStatus,
} from '../lib/setupTaskCopy';
// #1247 follow-on — the TL-invite modal is now a SHARED component used by both
// this dashboard panel and the focused /setup hub (SetupWizardPage), so the two
// surfaces can never drift on the invite wiring.
import InviteTechnicalLeadModal from './InviteTechnicalLeadModal';
// #1274 F4 / #1277 — connect_github opens this shared setup-scoped connect modal
// (member-safe) instead of navigating to the admin-only Settings control.
import SetupGithubConnectModal from './SetupGithubConnectModal';

// ── Types (mirror server payload shape from setupProjectPanelService) ─────────

interface PanelAssignee {
  userId:    string;
  name:      string;
  avatarUrl: string | null;
}

interface PanelTask {
  id:          string;
  taskType:    SetupTaskType;
  ring:        0 | 1 | 2 | 3;
  status:      SetupTaskStatus;
  assignee:    PanelAssignee | null;
  skipAllowed: boolean;
  startedAt:   string | null;
  completedAt: string | null;
}

interface PanelSetupProject {
  id:                  string;
  tenantId:            string;
  currentPhase:        0 | 1 | 2 | 3;
  status:              'in_progress' | 'paused';
  buyerUserId:         string;
  technicalLeadUserId: string | null;
  tierClass:           'free_trial' | 'growth' | 'individual';
  completionPct:       number;
  createdAt:           string;
  updatedAt:           string;
}

// ── ONB.B2-FIX.D2-FOLLOWON.D1 (#1124) — Cancel-invitation surface ──────────
// Backend returns the currently-PENDING invitation (if any) so the panel can
// render the Cancel button + confirmation modal. Field shape mirrors
// PendingSetupInvitation in setupProjectPanelService.ts.
interface PendingInvitation {
  id:           string;
  invitedEmail: string;
  invitedName:  string;
  invitedAt:    string;   // ISO timestamp
  expiresAt:    string;   // ISO timestamp
  inviterName:  string;
}

interface ActiveSetupProjectPanel {
  setupProject:      PanelSetupProject;
  tasks:             PanelTask[];
  pendingInvitation: PendingInvitation | null;
}

// Task copy + CTA mapping (TASK_COPY), PHASE_LABELS, and STATUS_PILL are now
// imported from ../lib/setupTaskCopy (shared with SetupWizardPage). Static
// literal paths only — never interpolate server data into the URL.

// ── Polling interval ────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 10_000;

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Selects the CURRENT-RING actionable tasks the panel should highlight.
 *
 * Ring-gated (epic #1247, fix #1 + the adjudicated brief correction): only
 * tasks whose ring === currentPhase are shown, so a later-ring task can NEVER
 * leak into the current-phase view. (Pre-fix this used selectNextThree — top-3
 * by status-then-ring sliced to 3 — which surfaced e.g. connect_github (ring 1)
 * inside the "Phase 1 of 4" view at phase 0. That cross-ring leak is the bug.)
 *
 * Completed + skipped never take a slot. Order within the ring:
 *   1. in_progress (loudest signal) → 2. pending → 3. blocked, tiebreak by id.
 *
 * The phase itself advances server-side, per-ring, automatically (recon R4b);
 * the 10s poll re-reads currentPhase, so when the current ring clears this
 * naturally re-renders to the next ring's tasks.
 */
export function selectCurrentRingTasks(
  tasks: PanelTask[],
  currentPhase: 0 | 1 | 2 | 3,
): PanelTask[] {
  const STATUS_RANK: Record<SetupTaskStatus, number> = {
    in_progress: 0,
    pending:     1,
    blocked:     2,
    completed:   99,
    skipped:     99,
  };

  return tasks
    .filter(t => t.ring === currentPhase && t.status !== 'completed' && t.status !== 'skipped')
    .sort((a, b) => {
      const rankDiff = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      if (rankDiff !== 0) return rankDiff;
      return a.id.localeCompare(b.id);
    });
}

function techLeadPresenceLine(tasks: PanelTask[], buyerUserId: string): string | null {
  const active = tasks.find(t =>
    t.status === 'in_progress' &&
    t.assignee !== null &&
    t.assignee.userId !== buyerUserId,
  );
  if (!active || !active.assignee) return null;
  const copy = TASK_COPY[active.taskType];
  return `${active.assignee.name} is ${copy.verb}…`;
}

// ── Component ───────────────────────────────────────────────────────────────

export default function OnboardingPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState<ActiveSetupProjectPanel | null | undefined>(undefined);
  // undefined = initial load; null = no active row (204 → auto-hide).

  // Tech Lead invite modal visibility (ONB.B1-FIX.A1). The modal IS the
  // destination for the invite_technical_lead task; its form + POST now live in
  // the shared InviteTechnicalLeadModal (#1247 follow-on). Open/close + refetch
  // stay here so the panel refreshes via its own poll.
  const [showInviteModal, setShowInviteModal] = useState(false);
  // GitHub connect modal visibility (#1274 F4 / #1277). Same shared component as
  // the /setup hub — connect_github opens it here too.
  const [showGithubModal, setShowGithubModal] = useState(false);

  // Cancel-invitation modal state (D1 #1124). Mirrors the invite-modal shape
  // above. The Cancel CTA renders only when there is a PENDING invitation
  // (data.pendingInvitation !== null) on an in_progress invite_technical_lead
  // task — see the task-row render below.
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling,      setCancelling]      = useState(false);
  const [cancelError,     setCancelError]     = useState<string | null>(null);

  // Cancellation flag that survives across renders and is readable from
  // every fetch callback. Setting `unmounted.current = true` in the
  // useEffect cleanup ensures that an in-flight fetch resolving AFTER
  // unmount will not call setState on a dead component. (Round 2
  // adversarial-review fix: closing the polling cleanup race that an
  // effect-scoped `let cancelled` could not close.)
  const unmounted = useRef(false);

  // Promise-chain shape (mirrors NotificationBell.tsx). The `react-hooks/
  // set-state-in-effect` rule flags setState calls reachable from `async`
  // functions inside an effect body — even when the call is gated behind
  // an `await` (i.e., never actually synchronous). Putting setData inside
  // a `.then(...)` callback puts it in a function expression the rule
  // does not trace into, satisfying the lint without changing runtime
  // semantics.
  const fetchPanel = useCallback((): Promise<void> => {
    return apiFetch<ActiveSetupProjectPanel | undefined>('/dashboard/setup-project')
      .then(res => {
        if (unmounted.current) return;
        setData(res ?? null);
      })
      .catch(() => {
        // Swallow — keep last successful state. No toast in V1.
      });
  }, []);

  // Initial fetch + polling.
  useEffect(() => {
    unmounted.current = false;
    void fetchPanel();
    const interval = setInterval(() => {
      if (unmounted.current) return;
      void fetchPanel();
    }, POLL_INTERVAL_MS);
    return () => {
      unmounted.current = true;
      clearInterval(interval);
    };
  }, [fetchPanel]);

  const handleSkip = useCallback((taskId: string): Promise<void> => {
    return apiFetch(`/setup-tasks/${taskId}/skip`, { method: 'PATCH' })
      .then(() => {
        if (unmounted.current) return;
        // Trigger an immediate refetch rather than waiting for the next tick.
        return fetchPanel();
      })
      .catch(() => {
        // No-op — leave previous state intact (memory: lean error handling).
      });
  }, [fetchPanel]);

  // The Tech Lead invite form + POST now live in the shared
  // InviteTechnicalLeadModal. On success it calls onSuccess (below): close the
  // modal + fire an immediate refetch (Q5 — the 10s poll would catch the status
  // change on its own tick anyway). Same behaviour as the former inline modal.
  const handleInviteSuccess = useCallback((): void => {
    if (unmounted.current) return;
    setShowInviteModal(false);
    void fetchPanel();
  }, [fetchPanel]);

  // #1274 F4 / #1277 — same close+refetch on a successful GitHub connect.
  const handleGithubSuccess = useCallback((): void => {
    if (unmounted.current) return;
    setShowGithubModal(false);
    void fetchPanel();
  }, [fetchPanel]);

  // Cancel the currently-PENDING setup invitation (D1 #1124). PATCHes the
  // new cancel endpoint, then immediately refetches the panel so the
  // dashboard pill returns to "Pending" without waiting for the next 10s
  // tick. On error (409 from concurrent cancel/accept, 403, 404), the
  // server message is surfaced inline and the modal stays open.
  const handleCancelInvitation = useCallback((): Promise<void> => {
    if (!data?.setupProject || !data?.pendingInvitation) return Promise.resolve();
    setCancelling(true);
    setCancelError(null);
    const setupProjectId = data.setupProject.id;
    const invitationId   = data.pendingInvitation.id;
    return apiFetch(
      `/setup-projects/${setupProjectId}/invitations/${invitationId}/cancel`,
      { method: 'PATCH' },
    )
      .then(() => {
        if (unmounted.current) return;
        setShowCancelModal(false);
        return fetchPanel();
      })
      .catch((err: unknown) => {
        if (unmounted.current) return;
        const msg = err instanceof Error
          ? err.message
          : 'Could not cancel invitation. Please try again.';
        setCancelError(msg);
      })
      .finally(() => {
        if (!unmounted.current) setCancelling(false);
      });
  }, [data, fetchPanel]);

  // ── Render guards ──────────────────────────────────────────────────────────

  // Initial load → render nothing rather than a flash of empty card.
  if (data === undefined) return null;
  // No active row → auto-hide.
  if (data === null) return null;

  const { setupProject, tasks } = data;
  const phaseLabel = PHASE_LABELS[setupProject.currentPhase];
  // Ring-gated to the current phase (fix #1). D4a: the panel persists as long
  // as GET /dashboard/setup-project returns a row — i.e. while status is
  // in_progress|paused (a 'completed' project 204s upstream and the panel
  // hides). Visibility is tied to that real completion signal, NOT to whether
  // this list is non-empty, so a momentarily-empty current ring (all tasks
  // terminal, pre phase-advance) still renders the panel until the next poll.
  const currentRingTasks = selectCurrentRingTasks(tasks, setupProject.currentPhase);
  const presenceLine = techLeadPresenceLine(tasks, setupProject.buyerUserId);
  const primary = currentRingTasks[0];

  return (
    <div
      data-testid="onboarding-panel"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 20,
        marginBottom: 24,
      }}
    >
      {/* Header row: phase + completion */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, margin: 0 }}>
            Setup: {phaseLabel}
          </h2>
          <p style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', margin: '4px 0 0 0' }}>
            Phase {setupProject.currentPhase + 1} of 4
          </p>
        </div>
        <span data-testid="completion-pct" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary, #6b7280)' }}>
          {setupProject.completionPct}%
        </span>
      </div>

      {/* Progress bar */}
      <div style={{ height: 6, borderRadius: 3, backgroundColor: 'var(--border)', marginBottom: 16 }}>
        <div
          data-testid="completion-bar"
          style={{
            height: '100%',
            borderRadius: 3,
            width: `${Math.min(100, setupProject.completionPct)}%`,
            backgroundColor: '#7c3aed',
            transition: 'width 300ms',
          }}
        />
      </div>

      {/* Tech Lead presence line (§11.3) */}
      {presenceLine && (
        <p data-testid="tech-lead-presence" style={{ fontSize: 13, color: '#d97706', margin: '0 0 12px 0', fontStyle: 'italic' }}>
          {presenceLine}
        </p>
      )}

      {/* Current-ring task list (ring-gated — no cross-ring leak) */}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {currentRingTasks.map(task => {
          const copy = TASK_COPY[task.taskType];
          const pill = STATUS_PILL[task.status];
          const showSkip = task.skipAllowed && task.status !== 'completed' && task.status !== 'skipped';
          const initials = task.assignee?.name
            ? task.assignee.name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()
            : '?';
          return (
            <li
              key={task.id}
              data-testid={`task-row-${task.taskType}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 0',
                borderTop: '1px solid var(--border)',
              }}
            >
              <div
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: task.assignee ? '#7c3aed' : 'var(--border)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <span style={{ flex: 1, fontSize: 14 }}>{copy.title}</span>
              <span
                data-testid={`task-status-${task.taskType}`}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: pill.bg,
                  color: pill.fg,
                }}
              >
                {pill.label}
              </span>
              {showSkip && (
                <button
                  data-testid={`task-skip-${task.taskType}`}
                  onClick={() => void handleSkip(task.id)}
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary, #6b7280)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 6px',
                  }}
                >
                  Skip for now
                </button>
              )}
              {/* Cancel-invitation CTA (D1 #1124). Renders only when:
                  (a) this is the invite_technical_lead task row,
                  (b) the task is in_progress (an invite was issued),
                  (c) there is a PENDING invitation to cancel (backend
                      confirmed via pendingInvitation enrichment).
                  Conditions (b) and (c) are usually paired but the panel
                  polls every 10s — between cancel commit and next poll,
                  the panel may see (b) true / (c) false briefly; the
                  button hides on (c)=false to avoid a 404-prone click. */}
              {task.taskType === 'invite_technical_lead'
                && task.status === 'in_progress'
                && data.pendingInvitation && (
                <button
                  data-testid="task-cancel-invitation"
                  onClick={() => {
                    setCancelError(null);
                    setShowCancelModal(true);
                  }}
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary, #6b7280)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 6px',
                  }}
                >
                  Cancel invitation
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Primary CTA — invite_technical_lead opens the modal; everything
          else navigates to its destination page per TASK_COPY. */}
      {primary && (
        <div style={{ marginTop: 16 }}>
          <button
            data-testid="primary-cta"
            onClick={() => {
              if (primary.taskType === 'invite_technical_lead') {
                setShowInviteModal(true);
              } else if (primary.taskType === 'connect_github') {
                // #1274 F4 / #1277 — opens the shared setup-scoped connect modal
                // (member-safe), not the admin-only Settings control.
                setShowGithubModal(true);
              } else {
                // ctaPath is optional (#1247 follow-on / #1274 — invite and
                // connect_github have none; they open modals above). Guard the
                // navigate so a task without a path is a no-op.
                const path = TASK_COPY[primary.taskType].ctaPath;
                if (path) navigate(path);
              }
            }}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              background: '#7c3aed',
              color: '#fff',
              border: 'none',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            {TASK_COPY[primary.taskType].ctaLabel}
          </button>
        </div>
      )}

      {/* Tech Lead invite modal — now the SHARED InviteTechnicalLeadModal
          (#1247 follow-on), identical to the one the /setup hub uses. On success
          it closes + refetches via handleInviteSuccess (the panel's 10s poll is
          the backstop). */}
      {showInviteModal && (
        <InviteTechnicalLeadModal
          setupProjectId={setupProject.id}
          onSuccess={handleInviteSuccess}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {/* GitHub connect modal (#1274 F4 / #1277) — shared with the /setup hub;
          the setup-scoped endpoint authorises the assigned TL / self-handling
          Buyer, so this works for the dashboard persona too. */}
      {showGithubModal && (
        <SetupGithubConnectModal
          onSuccess={handleGithubSuccess}
          onClose={() => setShowGithubModal(false)}
        />
      )}

      {/* Cancel-invitation confirmation modal (D1 #1124). Clones the invite
          modal's a11y shape: role="dialog", aria-modal="true",
          aria-labelledby on the title. Backdrop click dismisses unless an
          in-flight request is pending. */}
      {showCancelModal && data.pendingInvitation && (
        <div
          data-testid="cancel-invitation-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
          onClick={() => { if (!cancelling) setShowCancelModal(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-invite-title"
            style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
              padding: 24, width: 420, maxHeight: '85vh', overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 id="cancel-invite-title" style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
              Cancel this invitation?
            </h3>

            <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, margin: '0 0 12px 0' }}>
              This will invalidate the invitation sent to{' '}
              <strong>{data.pendingInvitation.invitedEmail}</strong>. They
              will be notified by email and the magic link will stop working.
            </p>

            {cancelError && (
              <p
                data-testid="cancel-invitation-error"
                style={{ fontSize: 13, color: '#ef4444', marginBottom: 12, marginTop: 0 }}
              >
                {cancelError}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                data-testid="cancel-invitation-dismiss"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
                style={{
                  padding: '8px 14px', borderRadius: 6,
                  background: 'transparent', color: 'var(--text-secondary, #6b7280)',
                  border: '1px solid var(--border)', cursor: cancelling ? 'not-allowed' : 'pointer',
                  fontSize: 14, opacity: cancelling ? 0.6 : 1,
                }}
              >
                Keep invitation
              </button>
              <button
                data-testid="cancel-invitation-confirm"
                onClick={() => void handleCancelInvitation()}
                disabled={cancelling}
                style={{
                  padding: '8px 14px', borderRadius: 6,
                  background: '#ef4444', color: '#fff',
                  border: 'none', fontWeight: 500,
                  cursor: cancelling ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: cancelling ? 0.6 : 1,
                }}
              >
                {cancelling ? 'Cancelling…' : 'Cancel invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
